import React, { useState, useEffect, useCallback, useRef } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import FormInput from "../../form-components/InputComponent";
import FormDatePicker from "../../form-components/DatePicker";
import { Col, Row, Modal, ModalBody, Card, Alert } from "reactstrap";
import { Table, Empty, Badge, InputNumber } from "antd";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import moment from "moment";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import superAdminService from "../../../api/super-admin/superAdminService";
import type {
  BundleDetail,
  BundleApp,
  BundleLinkedCustomer,
} from "../../../types/super-admin";

const BundleDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const formMethods = useForm({ mode: "onChange" });
  const { handleSubmit, setValue, reset, formState: { dirtyFields } } = formMethods;

  const [bundle, setBundle] = useState<BundleDetail | null>(null);
  const [linkedCustomers, setLinkedCustomers] = useState<BundleLinkedCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editApps, setEditApps] = useState<{ appId: number; appName: string; displayPrice: number | null; sortOrder: number }[]>([]);
  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [] as string[],
  });

  const fetchBundleDetails = useCallback(async () => {
    try {
      setLoading(true);
      const res = await superAdminService.getBundleById(id);
      if (res.status && res.data) {
        setBundle(res.data);
      }
    } catch (error: any) {
      console.error("Failed to fetch bundle:", error.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchLinkedCustomers = useCallback(async () => {
    try {
      const res = await superAdminService.getBundleLinkedCustomers(id);
      if (res.status && res.data) {
        setLinkedCustomers(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error: any) {
      console.error("Failed to fetch linked customers:", error.message);
    }
  }, [id]);

  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchBundleDetails();
    fetchLinkedCustomers();
  }, [fetchBundleDetails, fetchLinkedCustomers]);

  const handleActivate = async () => {
    try {
      const res = await superAdminService.activateBundle(id);
      if (res.status) {
        Swal.fire("Activated!", "Bundle has been activated.", "success");
        fetchBundleDetails();
      } else {
        Swal.fire("Error", res.message || "Failed to activate", "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    }
  };

  const handleDeactivate = async () => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will deactivate the bundle.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, deactivate!",
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await superAdminService.deactivateBundle(id);
          if (res.status) {
            Swal.fire("Deactivated!", "Bundle has been deactivated.", "success");
            fetchBundleDetails();
          } else {
            Swal.fire("Error", res.message || "Failed to deactivate", "error");
          }
        } catch (error: any) {
          Swal.fire("Error", error.message, "error");
        }
      }
    });
  };

  const handleUnassignCustomer = async (customerBundleId: number) => {
    Swal.fire({
      title: "Unassign Customer?",
      text: "This will remove the customer from this bundle.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, unassign!",
      allowOutsideClick: false,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-primary",
        cancelButton: "btn btn-secondary",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await superAdminService.unassignBundleFromCustomer({
            customerBundleId,
          });
          if (res.status) {
            Swal.fire("Unassigned!", "Customer has been unassigned.", "success");
            fetchLinkedCustomers();
          } else {
            Swal.fire("Error", res.message || "Failed to unassign", "error");
          }
        } catch (error: any) {
          Swal.fire("Error", error.message, "error");
        }
      }
    });
  };

  const openEditModal = () => {
    if (!bundle) return;
    setAlertInfo({ type: "", message: "", errors: [] });
    setValue("bundleName", bundle.bundleName || "");
    setValue("description", bundle.description || "");
    setValue("bundlePrice", bundle.bundlePrice || "");
    setValue("validTo", bundle.validTo ? dayjs(bundle.validTo) : undefined);
    setEditApps(
      (bundle.apps || []).map((a) => ({
        appId: a.appId,
        appName: a.appCode ? `${a.appCode} - ${a.appName}` : a.appName,
        displayPrice: a.displayPrice,
        sortOrder: a.sortOrder,
      }))
    );
    setEditModal(true);
  };

  const updateEditAppField = (appId: number, field: string, value: any) => {
    setEditApps((prev) =>
      prev.map((a) => (a.appId === appId ? { ...a, [field]: value } : a))
    );
  };

  const submitEdit = async (data: any) => {
    if (!bundle) return;
    setEditLoading(true);
    setAlertInfo({ type: "", message: "", errors: [] });

    try {
      const payload: any = { bundleId: bundle.bundleId };

      if (dirtyFields.bundleName) payload.bundleName = data.bundleName?.trim();
      if (dirtyFields.description) payload.description = typeof data.description === "string" ? data.description.trim() : data.description;
      if (dirtyFields.bundlePrice) payload.bundlePrice = Number(data.bundlePrice);
      if (dirtyFields.validTo) payload.validTo = new Date(data.validTo).toLocaleDateString("en-CA");

      // Include only changed app display price/sortOrder
      if (editApps.length > 0) {
        const originalApps = bundle.apps || [];
        const changedApps = editApps.filter((ea) => {
          const orig = originalApps.find((o) => o.appId === ea.appId);
          if (!orig) return true;
          return ea.displayPrice !== orig.displayPrice || ea.sortOrder !== orig.sortOrder;
        });
        if (changedApps.length > 0) {
          payload.apps = changedApps.map((a) => ({
            appId: a.appId,
            displayPrice: a.displayPrice,
            sortOrder: a.sortOrder,
          }));
        }
      }

      // Use all editApps for validation (not just changed ones)
      if (editApps.length > 0) {

        // Validate display price sum vs bundle price
        const effectivePrice = payload.bundlePrice ?? Number(bundle.bundlePrice || 0);
        if (effectivePrice > 0) {
          const displayPriceSum = editApps.reduce((sum, a) => sum + (a.displayPrice || 0), 0);
          if (displayPriceSum > effectivePrice) {
            setAlertInfo({
              type: "error",
              message: `Sum of app display prices (${displayPriceSum}) cannot exceed the bundle price (${effectivePrice}).`,
              errors: [],
            });
            setEditLoading(false);
            return;
          }
        }
      }

      if (Object.keys(payload).length === 1) {
        setAlertInfo({ type: "info", message: "No changes detected.", errors: [] });
        setEditLoading(false);
        return;
      }

      const res = await superAdminService.updateBundle(payload);
      if (res.status) {
        Swal.fire("Updated!", "Bundle has been updated.", "success");
        setEditModal(false);
        reset();
        fetchBundleDetails();
      } else {
        const errorMsg = Array.isArray(res.message)
          ? res.message.join(", ")
          : res.message || "An error occurred";
        setAlertInfo({ type: "error", message: errorMsg, errors: [] });
      }
    } catch (error: any) {
      setAlertInfo({
        type: "error",
        message: error.message || "An error occurred",
        errors: [],
      });
    } finally {
      setEditLoading(false);
    }
  };

  const appColumns = [
    {
      title: "Application",
      dataIndex: "appName",
      key: "appName",
      render: (text: string, record: BundleApp) =>
        record.appCode ? `${record.appCode} - ${text}` : text,
    },
    {
      title: "Display Price",
      dataIndex: "displayPrice",
      key: "displayPrice",
      render: (val: number | null) =>
        val != null ? Number(val).toFixed(2) : "-",
    },
    {
      title: "Sort Order",
      dataIndex: "sortOrder",
      key: "sortOrder",
    },
  ];

  const customerColumns = [
    {
      title: "Customer Code",
      dataIndex: "customerCode",
      key: "customerCode",
      width: 150,
      ellipsis: true,
      render: (text: string, record: BundleLinkedCustomer) =>
        text ? (
          <Link
            to={`/sa-customers/${record.customerId}/control-panel`}
            className="link link-primary"
          >
            {text}
          </Link>
        ) : "-",
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
      width: 220,
      ellipsis: true,
      render: (text: string, record: BundleLinkedCustomer) =>
        text ? (
          <Link
            to={`/sa-customers/${record.customerId}/control-panel`}
            className="link link-primary"
          >
            {text}
          </Link>
        ) : "-",
    },
    {
      title: "Effective Price",
      dataIndex: "effectivePrice",
      key: "effectivePrice",
      width: 130,
      align: "right" as const,
      render: (val: number | null) =>
        val != null ? Number(val).toFixed(2) : "-",
    },
    {
      title: "Assigned At",
      dataIndex: "assignedAt",
      key: "assignedAt",
      width: 130,
      render: (date: string) => moment(date).format("DD MMM YYYY"),
    },
    {
      title: "Valid From",
      dataIndex: "validFrom",
      key: "validFrom",
      width: 130,
      render: (date: string) => moment(date).format("DD MMM YYYY"),
    },
    {
      title: "Valid To",
      dataIndex: "validTo",
      key: "validTo",
      width: 130,
      render: (date: string) => moment(date).format("DD MMM YYYY"),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      width: 110,
      align: "center" as const,
      render: (isActive: boolean) => (
        <span className={`tb-status text-${isActive ? "success" : "danger"}`}>
          {isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, record: BundleLinkedCustomer) =>
        record.isActive ? (
          <a
            href="#unassign"
            className="link link-danger"
            onClick={(e) => {
              e.preventDefault();
              handleUnassignCustomer(record.customerBundleId);
            }}
          >
            Unassign
          </a>
        ) : null,
    },
  ];

  if (loading) {
    return (
      <React.Fragment>
        <Head title="Enterprise Pricing Details" />
        <Content>
          <LoadingSpinner height="300px" />
        </Content>
      </React.Fragment>
    );
  }

  if (!bundle) {
    return (
      <React.Fragment>
        <Head title="Enterprise Pricing Details" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Bundle not found" />
            <Button
              color="primary"
              className="mt-3"
              onClick={() => navigate("/sa-bundles")}
            >
              Back to List
            </Button>
          </div>
        </Content>
      </React.Fragment>
    );
  }

  const pricingTypeColor: Record<string, string> = {
    Flat: "blue",
    "User-based": "cyan",
    "Tier-based": "purple",
  };

  return (
    <React.Fragment>
      <Head title="Enterprise Pricing Details" />
      <Content>
        {/* Header */}
        <BlockHead size="sm">
          <BlockBetween className="g-3">
            <BlockHeadContent>
              <BlockTitle page>
                {bundle.bundleCode} — {bundle.bundleName}{" "}
                <Badge color={bundle.status === "Active" ? "green" : "red"}>
                  {bundle.status}
                </Badge>
              </BlockTitle>
              <BlockDes className="text-soft">
                <ul className="list-inline">
                  <li>
                    Type:{" "}
                    <Badge
                      color={pricingTypeColor[bundle.pricingType] || "default"}
                    >
                      {bundle.pricingType}
                    </Badge>
                  </li>
                  <li>
                    Apps: <span className="text-base">{bundle.apps?.length || 0}</span>
                  </li>
                </ul>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <ul className="nk-block-tools g-3">
                <li>
                  <Link to="/sa-bundles">
                    <Button outline className="bg-white d-none d-sm-inline-flex">
                      <Icon name="arrow-left"></Icon>
                      <span>Back</span>
                    </Button>
                  </Link>
                </li>
                <li>
                  <Button color="primary" onClick={openEditModal}>
                    <Icon name="edit-alt-fill"></Icon>
                    <span>Edit</span>
                  </Button>
                </li>
                <li>
                  {bundle.status === "Active" ? (
                    <Button color="danger" onClick={handleDeactivate}>
                      <Icon name="na"></Icon>
                      <span>Deactivate</span>
                    </Button>
                  ) : (
                    <Button color="success" onClick={handleActivate}>
                      <Icon name="check-circle"></Icon>
                      <span>Activate</span>
                    </Button>
                  )}
                </li>
              </ul>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {/* Bundle Info */}
        <Block>
          <Row className="gy-5">
            <Col lg="6">
              <BlockHead>
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h5">Bundle Information</BlockTitle>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>
              <Card className="card-bordered">
                <ul className="data-list is-compact">
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Bundle Code</div>
                      <div className="data-value">{bundle.bundleCode}</div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Bundle Name</div>
                      <div className="data-value">{bundle.bundleName}</div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Description</div>
                      <div className="data-value">{bundle.description || "-"}</div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Currency</div>
                      <div className="data-value">{bundle.currencyCode || "-"}</div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Pricing Type</div>
                      <div className="data-value">
                        <Badge color={pricingTypeColor[bundle.pricingType] || "default"}>
                          {bundle.pricingType}
                        </Badge>
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Bundle Price</div>
                      <div className="data-value">
                        {bundle.bundlePrice != null
                          ? Number(bundle.bundlePrice).toFixed(2)
                          : "-"}
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Billing Frequency</div>
                      <div className="data-value">{bundle.billingFrequency || "-"}</div>
                    </div>
                  </li>
                </ul>
              </Card>
            </Col>
            <Col lg="6">
              <BlockHead>
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h5">Location & Validity</BlockTitle>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>
              <Card className="card-bordered">
                <ul className="data-list is-compact">
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Country</div>
                      <div className="data-value">{bundle.countryName || "Global"}</div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">State</div>
                      <div className="data-value">{bundle.stateName || "-"}</div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Valid From</div>
                      <div className="data-value">
                        {moment(bundle.validFrom).format("DD MMM YYYY")}
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Valid To</div>
                      <div className="data-value">
                        {moment(bundle.validTo).format("DD MMM YYYY")}
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Status</div>
                      <div className="data-value">
                        <span
                          className={`tb-status text-${
                            bundle.status === "Active" ? "success" : "danger"
                          }`}
                        >
                          {bundle.status}
                        </span>
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Created</div>
                      <div className="data-value">
                        {moment.utc(bundle.createdAt).local().format("DD MMM YYYY hh:mm A")}
                      </div>
                    </div>
                  </li>
                  <li className="data-item">
                    <div className="data-col">
                      <div className="data-label">Updated</div>
                      <div className="data-value">
                        {moment.utc(bundle.updatedAt).local().format("DD MMM YYYY hh:mm A")}
                      </div>
                    </div>
                  </li>
                </ul>
              </Card>
            </Col>
          </Row>
        </Block>

        {/* Apps Table */}
        <Block>
          <BlockHead size="sm">
            <BlockTitle tag="h5">
              Applications ({bundle.apps?.length || 0})
            </BlockTitle>
          </BlockHead>
          <Card className="card-bordered">
            <div className="card-inner">
              {bundle.apps && bundle.apps.length > 0 ? (
                <Table
                  columns={appColumns}
                  dataSource={bundle.apps}
                  rowKey="bundleModuleId"
                  pagination={false}
                  bordered
                  size="middle"
                />
              ) : (
                <Empty description="No apps in this bundle" />
              )}
            </div>
          </Card>
        </Block>

        {/* Linked Customers */}
        <Block>
          <BlockHead size="sm">
            <BlockTitle tag="h5">
              Linked Customers ({linkedCustomers.length})
            </BlockTitle>
          </BlockHead>
          <Card className="card-bordered">
            <div className="card-inner">
              {linkedCustomers.length > 0 ? (
                <Table
                  columns={customerColumns}
                  dataSource={linkedCustomers}
                  rowKey="customerBundleId"
                  scroll={{ x: "max-content", y: 300 }}
                  pagination={{ pageSize: 10 }}
                  bordered
                  size="middle"
                />
              ) : (
                <Empty description="No customers linked to this bundle" />
              )}
            </div>
          </Card>
        </Block>
      </Content>

      {/* Edit Modal */}
      <Modal
        isOpen={editModal}
        toggle={() => setEditModal(false)}
        className="modal-dialog-centered"
        size="lg"
        backdrop="static"
      >
        <ModalBody>
          <a
            href="#cancel"
            onClick={(e) => {
              e.preventDefault();
              setEditModal(false);
              reset();
            }}
            className="close"
          >
            <Icon name="cross-sm"></Icon>
          </a>
          <div className="p-2">
            {alertInfo.message && (
              <div className="mb-3 mt-1">
                <Alert
                  color={alertInfo.type === "error" ? "danger" : "success"}
                  className="alert-icon alert-animated"
                >
                  <strong>{alertInfo.message}</strong>
                  <Icon
                    name={
                      alertInfo.type === "error"
                        ? "alert-circle"
                        : "check-circle"
                    }
                  />
                </Alert>
              </div>
            )}

            <h5 className="title">Edit Bundle</h5>
            <div className="mt-4">
              {/* Non-editable fields shown as read-only info */}
              <Row className="gy-2 mb-3">
                <Col sm="6">
                  <small className="text-soft d-block">Bundle Code</small>
                  <strong>{bundle.bundleCode}</strong>
                </Col>
                <Col sm="6">
                  <small className="text-soft d-block">Currency</small>
                  <strong>{bundle.currencyCode || "-"}</strong>
                </Col>
                <Col sm="6">
                  <small className="text-soft d-block">Pricing Type</small>
                  <strong>{bundle.pricingType || "-"}</strong>
                </Col>
                <Col sm="6">
                  <small className="text-soft d-block">Billing Frequency</small>
                  <strong>{bundle.billingFrequency || "-"}</strong>
                </Col>
                <Col sm="6">
                  <small className="text-soft d-block">Country</small>
                  <strong>{bundle.countryName || "Global"}</strong>
                </Col>
                <Col sm="6">
                  <small className="text-soft d-block">State</small>
                  <strong>{bundle.stateName || "-"}</strong>
                </Col>
                <Col sm="6">
                  <small className="text-soft d-block">Valid From</small>
                  <strong>{moment(bundle.validFrom).format("DD MMM YYYY")}</strong>
                </Col>
              </Row>
              <hr className="mt-0 mb-3" />

              <FormProvider {...formMethods}>
                <form
                  className="content clearfix"
                  onSubmit={handleSubmit(submitEdit)}
                >
                  <Row className="gy-4">
                    <Col lg="12">
                      <FormInput
                        name="bundleName"
                        label="Bundle Name"
                        type="text"
                        required
                        maxLength={200}
                        placeholder="Enter bundle name"
                      />
                    </Col>
                    <Col lg="12">
                      <FormInput
                        name="description"
                        label="Description"
                        type="text"
                        maxLength={500}
                        placeholder="Enter description"
                      />
                    </Col>
                    <Col lg="12">
                      <FormInput
                        name="bundlePrice"
                        label="Bundle Price"
                        type="number"
                        placeholder="Enter bundle price"
                      />
                    </Col>
                    {editApps.length > 0 && (
                      <Col lg="12">
                        <label className="form-label">App Display Prices</label>
                        <Table
                          columns={[
                            {
                              title: "Application",
                              dataIndex: "appName",
                              key: "appName",
                              render: (text: string) => <strong>{text}</strong>,
                            },
                            {
                              title: "Display Price",
                              dataIndex: "displayPrice",
                              key: "displayPrice",
                              width: 160,
                              render: (_: any, record: any) => (
                                <InputNumber
                                  size="small"
                                  min={0}
                                  precision={2}
                                  placeholder="0.00"
                                  value={record.displayPrice}
                                  onChange={(val) => updateEditAppField(record.appId, "displayPrice", val)}
                                  style={{ width: "100%" }}
                                />
                              ),
                            },
                            {
                              title: "Sort Order",
                              dataIndex: "sortOrder",
                              key: "sortOrder",
                              width: 100,
                              align: "center" as const,
                              render: (_: any, record: any) => (
                                <InputNumber
                                  size="small"
                                  min={1}
                                  value={record.sortOrder}
                                  onChange={(val) => updateEditAppField(record.appId, "sortOrder", val || 1)}
                                  style={{ width: 70 }}
                                />
                              ),
                            },
                          ]}
                          dataSource={editApps}
                          rowKey="appId"
                          pagination={false}
                          bordered
                          size="small"
                        />
                      </Col>
                    )}
                    <Col lg="12">
                      <FormDatePicker
                        name="validTo"
                        label="Valid To"
                        required
                        placeholder="Select end date"
                        minDate={(() => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const vf = bundle.validFrom
                            ? new Date(bundle.validFrom)
                            : today;
                          return vf > today ? vf : today;
                        })()}
                      />
                    </Col>
                  </Row>

                  <Row className="mt-4">
                    <Col lg="12">
                      <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                        <li>
                          <Button color="primary" size="md" type="submit">
                            {editLoading ? <LoadingSpinner size="sm" inline /> : "Save Changes"}
                          </Button>
                        </li>
                        <li>
                          <a
                            href="#cancel"
                            onClick={(e) => {
                              e.preventDefault();
                              if (!editLoading) {
                                setEditModal(false);
                                reset();
                              }
                            }}
                            className={`link link-light${editLoading ? " disabled text-muted" : ""}`}
                            style={editLoading ? { pointerEvents: "none" } : {}}
                          >
                            Cancel
                          </a>
                        </li>
                      </ul>
                    </Col>
                  </Row>
                </form>
              </FormProvider>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default BundleDetails;
